/**
 * Patient Demographics Step Component
 * 
 * First step in the MedPass workflow - collects and verifies patient information,
 * medical history, allergies, and contact details.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    DatePicker,
    Select,
    Button,
    Alert,
    Divider,
    Space,
    Tag,
    List,
    Modal,
    message,
    Tooltip
} from 'antd';
import {
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    HomeOutlined,
    MedicineBoxOutlined,
    ExclamationCircleOutlined,
    PlusOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import moment from 'moment';

import patientService from '../../../services/patientService';
import medpassService from '../../../services/medpassService';

const { Option } = Select;
const { TextArea } = Input;

const PatientDemographicsStep = ({ workflow, onSave, onNext, onPrev, loading }) => {
    const [form] = Form.useForm();
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const [patientSearchResults, setPatientSearchResults] = useState([]);
    const [showPatientSearch, setShowPatientSearch] = useState(false);
    const [allergies, setAllergies] = useState([]);
    const [medicalConditions, setMedicalConditions] = useState([]);
    const [currentMedications, setCurrentMedications] = useState([]);
    const [emergencyContacts, setEmergencyContacts] = useState([]);
    const [newAllergy, setNewAllergy] = useState('');
    const [newCondition, setNewCondition] = useState('');
    const [newMedication, setNewMedication] = useState('');

    // Initialize form with existing data and load all patients
    useEffect(() => {
        if (workflow?.patient_profile) {
            const profile = workflow.patient_profile;
            
            form.setFieldsValue({
                patient_id: profile.patient_id,
                mrn: profile.mrn,
                full_name: profile.full_name,
                date_of_birth: profile.date_of_birth ? moment(profile.date_of_birth) : null,
                age: profile.age,
                gender: profile.gender,
                phone_primary: profile.phone_primary,
                phone_secondary: profile.phone_secondary,
                email: profile.email,
                insurance_provider: profile.insurance_provider,
                insurance_id: profile.insurance_id,
                policy_number: profile.policy_number,
                primary_physician: profile.primary_physician,
                assigned_nurse: profile.assigned_nurse,
                care_coordinator: profile.care_coordinator
            });

            setAllergies(profile.allergies || []);
            setMedicalConditions(profile.medical_conditions || []);
            setCurrentMedications(profile.current_medications || []);
            setEmergencyContacts(profile.emergency_contacts || []);
        }

        // Load all patients initially
        loadAllPatients();
    }, [workflow, form]);

    // Load all patients for selection
    const loadAllPatients = async () => {
        setPatientSearchLoading(true);
        try {
            const result = await patientService.getPatients({
                limit: 50, // Show first 50 patients
                skip: 0
            });

            if (result.success) {
                // Backend returns data under 'patients' key
                setPatientSearchResults(result.data?.patients || []);
            } else {
                message.error('Failed to load patients');
                setPatientSearchResults([]);
            }
        } catch (error) {
            console.error('Error loading patients:', error);
            message.error('Failed to load patients');
            setPatientSearchResults([]);
        } finally {
            setPatientSearchLoading(false);
        }
    };

    // Calculate age when date of birth changes
    const handleDateOfBirthChange = (date) => {
        if (date) {
            const age = moment().diff(date, 'years');
            form.setFieldValue('age', age);
        } else {
            form.setFieldValue('age', 0);
        }
    };

    // Search for existing patients
    const searchPatients = async (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            // If no search term, load all patients
            loadAllPatients();
            return;
        }

        if (searchTerm.length < 2) {
            return; // Wait for at least 2 characters
        }

        setPatientSearchLoading(true);
        try {
            const result = await patientService.searchPatients({
                query: searchTerm,
                limit: 20
            });

            if (result.success) {
                // Backend returns data under 'patients' key
                setPatientSearchResults(result.data?.patients || []);
            } else {
                message.error('Search failed');
                setPatientSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching patients:', error);
            message.error('Error searching patients');
            setPatientSearchResults([]);
        } finally {
            setPatientSearchLoading(false);
        }
    };

    // Select patient from search results
    const selectPatient = async (patient) => {
        // The search returns PatientSummary, we need to fetch full patient details
        try {
            setPatientSearchLoading(true);
            
            // Get full patient details by ID
            const fullPatientResult = await patientService.getPatientById(patient.id);
            
            if (!fullPatientResult.success) {
                message.error('Failed to load full patient details');
                return;
            }
            
            const fullPatient = fullPatientResult.data;
            
            // Auto-fill form with patient data
            const patientData = {
                patient_id: fullPatient.id || patient.id,
                mrn: patient.mrn || 'No MRN',
                full_name: patient.full_name || 'Unnamed Patient',
                date_of_birth: fullPatient.demographics?.date_of_birth ? moment(fullPatient.demographics.date_of_birth) : null,
                age: patient.age || 0,
                gender: patient.gender || '',
                phone_primary: fullPatient.demographics?.phone_primary || '',
                phone_secondary: fullPatient.demographics?.phone_secondary || '',
                email: fullPatient.demographics?.email || '',
                insurance_provider: fullPatient.medical_record?.insurance_info?.[0]?.provider || '',
                insurance_id: fullPatient.medical_record?.insurance_info?.[0]?.policy_number || '',
                policy_number: fullPatient.medical_record?.insurance_info?.[0]?.policy_number || ''
            };

            form.setFieldsValue(patientData);

            // Load patient's medical history from full patient data
            if (fullPatient.medical_record) {
                // Load allergies - remove duplicates
                const patientAllergies = [...new Set(fullPatient.medical_record.allergies?.map(a => 
                    typeof a === 'string' ? a : a.allergen
                ) || [])];
                setAllergies(patientAllergies);

                // Load medical conditions - remove duplicates
                const patientConditions = [...new Set(fullPatient.medical_record.medical_conditions?.map(c => 
                    typeof c === 'string' ? c : c.condition
                ) || [])];
                setMedicalConditions(patientConditions);

                // Load current medications - remove duplicates
                const patientMedications = [...new Set(fullPatient.medical_record.current_medications?.map(m => 
                    typeof m === 'string' ? m : m.medication
                ) || [])];
                setCurrentMedications(patientMedications);
            } else {
                // Clear medical history if not available
                setAllergies([]);
                setMedicalConditions([]);
                setCurrentMedications([]);
            }

            // Load care team information if available
            if (fullPatient.care_status?.assigned_staff) {
                const primaryPhysician = fullPatient.care_status.assigned_staff.find(s => 
                    s.assignment_type === 'primary' && s.role.toLowerCase().includes('doctor')
                );
                const primaryNurse = fullPatient.care_status.assigned_staff.find(s => 
                    s.assignment_type === 'primary' && s.role.toLowerCase().includes('nurse')
                );
                const coordinator = fullPatient.care_status.assigned_staff.find(s => 
                    s.role.toLowerCase().includes('coordinator')
                );

                if (primaryPhysician || primaryNurse || coordinator) {
                    form.setFieldsValue({
                        primary_physician: primaryPhysician?.user_id || '',
                        assigned_nurse: primaryNurse?.user_id || '',
                        care_coordinator: coordinator?.user_id || ''
                    });
                }
            }

            setShowPatientSearch(false);
            message.success(`Patient information loaded for ${patient.full_name || 'selected patient'}`);
            
        } catch (error) {
            console.error('Error loading patient details:', error);
            message.error('Failed to load patient details');
        } finally {
            setPatientSearchLoading(false);
        }
    };

    // Add allergy
    const addAllergy = () => {
        if (newAllergy.trim() && !allergies.includes(newAllergy.trim())) {
            setAllergies([...allergies, newAllergy.trim()]);
            setNewAllergy('');
        }
    };

    // Remove allergy
    const removeAllergy = (allergy) => {
        setAllergies(allergies.filter(a => a !== allergy));
    };

    // Add medical condition
    const addMedicalCondition = () => {
        if (newCondition.trim() && !medicalConditions.includes(newCondition.trim())) {
            setMedicalConditions([...medicalConditions, newCondition.trim()]);
            setNewCondition('');
        }
    };

    // Remove medical condition
    const removeMedicalCondition = (condition) => {
        setMedicalConditions(medicalConditions.filter(c => c !== condition));
    };

    // Add current medication
    const addCurrentMedication = () => {
        if (newMedication.trim() && !currentMedications.includes(newMedication.trim())) {
            setCurrentMedications([...currentMedications, newMedication.trim()]);
            setNewMedication('');
        }
    };

    // Remove current medication
    const removeCurrentMedication = (medication) => {
        setCurrentMedications(currentMedications.filter(m => m !== medication));
    };

    // Save step data
    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            const patientProfile = {
                patient_id: values.patient_id || null, // Always include patient_id, null if not set
                mrn: values.mrn,
                full_name: values.full_name,
                date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
                age: values.age,
                gender: values.gender,
                phone_primary: values.phone_primary,
                phone_secondary: values.phone_secondary,
                email: values.email,
                home_address: values.home_address || {},
                service_address: values.service_address || {},
                insurance_provider: values.insurance_provider,
                insurance_id: values.insurance_id,
                policy_number: values.policy_number,
                allergies: [...new Set(allergies)], // Remove duplicates
                medical_conditions: [...new Set(medicalConditions)], // Remove duplicates
                current_medications: [...new Set(currentMedications)], // Remove duplicates
                emergency_contacts: emergencyContacts,
                primary_physician: values.primary_physician,
                assigned_nurse: values.assigned_nurse,
                care_coordinator: values.care_coordinator
            };

            // Validate data
            const validation = medpassService.validatePatientProfile(patientProfile);
            if (!validation.isValid) {
                message.error(validation.errors[0]);
                return;
            }

            const success = await onSave(patientProfile, 'patient_profile');
            if (success) {
                message.success('Patient demographics saved successfully');
                return true;
            }
            return false;
        } catch (error) {
            message.error('Please complete all required fields');
            return false;
        }
    };

    const handleNext = async () => {
        const success = await handleSave();
        if (success && onNext) {
            // Add a small delay to ensure workflow state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            await onNext();
        }
    };

    return (
        <div className="patient-demographics-step">
            <Card 
                title={
                    <Space>
                        <UserOutlined />
                        Patient Demographics
                    </Space>
                }
                extra={
                    <Button 
                        type="primary" 
                        ghost 
                        onClick={() => setShowPatientSearch(true)}
                        icon={<UserOutlined />}
                    >
                        Search Existing Patient
                    </Button>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleNext}
                    disabled={loading}
                >
                    {/* Hidden field for patient_id */}
                    <Form.Item name="patient_id" style={{ display: 'none' }}>
                        <Input type="hidden" />
                    </Form.Item>

                    {/* Basic Information */}
                    <Card type="inner" title="Basic Information" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Medical Record Number (MRN)"
                                    name="mrn"
                                    rules={[{ required: true, message: 'MRN is required' }]}
                                >
                                    <Input placeholder="Enter MRN" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Full Name"
                                    name="full_name"
                                    rules={[{ required: true, message: 'Full name is required' }]}
                                >
                                    <Input placeholder="Enter full name" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Date of Birth"
                                    name="date_of_birth"
                                >
                                    <DatePicker 
                                        style={{ width: '100%' }}
                                        placeholder="Select date of birth"
                                        onChange={handleDateOfBirthChange}
                                    />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Age"
                                    name="age"
                                >
                                    <Input type="number" placeholder="Age" disabled />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Gender"
                                    name="gender"
                                >
                                    <Select placeholder="Select gender">
                                        <Option value="male">Male</Option>
                                        <Option value="female">Female</Option>
                                        <Option value="other">Other</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Contact Information */}
                    <Card type="inner" title="Contact Information" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Primary Phone"
                                    name="phone_primary"
                                >
                                    <Input 
                                        prefix={<PhoneOutlined />}
                                        placeholder="Primary phone number" 
                                    />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Secondary Phone"
                                    name="phone_secondary"
                                >
                                    <Input 
                                        prefix={<PhoneOutlined />}
                                        placeholder="Secondary phone number" 
                                    />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Email"
                                    name="email"
                                    rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                                >
                                    <Input 
                                        prefix={<MailOutlined />}
                                        placeholder="Email address" 
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Insurance Information */}
                    <Card type="inner" title="Insurance Information" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Insurance Provider"
                                    name="insurance_provider"
                                >
                                    <Input placeholder="Insurance provider name" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Insurance ID"
                                    name="insurance_id"
                                >
                                    <Input placeholder="Insurance ID number" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Policy Number"
                                    name="policy_number"
                                >
                                    <Input placeholder="Policy number" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Medical History */}
                    <Card type="inner" title="Medical History" style={{ marginBottom: 16 }}>
                        {/* Allergies */}
                        <div style={{ marginBottom: 16 }}>
                            <h4>Known Allergies</h4>
                            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                                <Input
                                    value={newAllergy}
                                    onChange={(e) => setNewAllergy(e.target.value)}
                                    placeholder="Add allergy"
                                    onPressEnter={addAllergy}
                                />
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={addAllergy}
                                >
                                    Add
                                </Button>
                            </Space.Compact>
                            
                            <div>
                                {allergies.map((allergy, index) => (
                                    <Tag
                                        key={index}
                                        color="red"
                                        closable
                                        onClose={() => removeAllergy(allergy)}
                                        style={{ marginBottom: 4 }}
                                    >
                                        {allergy}
                                    </Tag>
                                ))}
                                {allergies.length === 0 && (
                                    <Tag color="default">No known allergies</Tag>
                                )}
                            </div>
                        </div>

                        {/* Medical Conditions */}
                        <div style={{ marginBottom: 16 }}>
                            <h4>Medical Conditions</h4>
                            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                                <Input
                                    value={newCondition}
                                    onChange={(e) => setNewCondition(e.target.value)}
                                    placeholder="Add medical condition"
                                    onPressEnter={addMedicalCondition}
                                />
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={addMedicalCondition}
                                >
                                    Add
                                </Button>
                            </Space.Compact>
                            
                            <div>
                                {medicalConditions.map((condition, index) => (
                                    <Tag
                                        key={index}
                                        color="orange"
                                        closable
                                        onClose={() => removeMedicalCondition(condition)}
                                        style={{ marginBottom: 4 }}
                                    >
                                        {condition}
                                    </Tag>
                                ))}
                                {medicalConditions.length === 0 && (
                                    <Tag color="default">No medical conditions recorded</Tag>
                                )}
                            </div>
                        </div>

                        {/* Current Medications */}
                        <div>
                            <h4>Current Medications</h4>
                            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                                <Input
                                    value={newMedication}
                                    onChange={(e) => setNewMedication(e.target.value)}
                                    placeholder="Add current medication"
                                    onPressEnter={addCurrentMedication}
                                />
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={addCurrentMedication}
                                >
                                    Add
                                </Button>
                            </Space.Compact>
                            
                            <div>
                                {currentMedications.map((medication, index) => (
                                    <Tag
                                        key={index}
                                        color="blue"
                                        closable
                                        onClose={() => removeCurrentMedication(medication)}
                                        style={{ marginBottom: 4 }}
                                    >
                                        {medication}
                                    </Tag>
                                ))}
                                {currentMedications.length === 0 && (
                                    <Tag color="default">No current medications</Tag>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Care Team */}
                    <Card type="inner" title="Care Team" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Primary Physician"
                                    name="primary_physician"
                                >
                                    <Input placeholder="Primary physician name" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Assigned Nurse"
                                    name="assigned_nurse"
                                >
                                    <Input placeholder="Assigned nurse name" />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Care Coordinator"
                                    name="care_coordinator"
                                >
                                    <Input placeholder="Care coordinator name" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Validation Alerts */}
                    {allergies.length === 0 && (
                        <Alert
                            message="No allergies documented"
                            description="Please verify with patient and document any known allergies to prevent adverse reactions."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Action Buttons */}
                    <Row justify="space-between" style={{ marginTop: 24 }}>
                        <Col>
                            <Button onClick={onPrev} disabled={!onPrev}>
                                Previous
                            </Button>
                        </Col>
                        
                        <Col>
                            <Button type="primary" onClick={handleNext} loading={loading}>
                                Save & Next Step
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Patient Search Modal */}
            <Modal
                title={
                    <Space>
                        <UserOutlined />
                        Search & Select Patient
                    </Space>
                }
                open={showPatientSearch}
                onCancel={() => setShowPatientSearch(false)}
                footer={null}
                width={900}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder="Search by name, MRN, or phone number (leave empty to show all patients)"
                        onSearch={searchPatients}
                        onChange={(e) => {
                            // Auto-search as user types, but with delay
                            const value = e.target.value;
                            if (value.length === 0) {
                                loadAllPatients();
                            }
                        }}
                        loading={patientSearchLoading}
                        allowClear
                        size="large"
                    />
                    <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                        {patientSearchResults.length} patient(s) found
                    </div>
                </div>
                
                <List
                    dataSource={patientSearchResults}
                    loading={patientSearchLoading}
                    renderItem={(patient) => {
                        const fullName = patient.full_name || 'Unnamed Patient';
                        const mrn = patient.mrn || 'No MRN';
                        const age = patient.age || 'N/A';
                        
                        return (
                            <List.Item
                                actions={[
                                    <Button 
                                        type="primary" 
                                        size="small"
                                        onClick={() => selectPatient(patient)}
                                    >
                                        Select Patient
                                    </Button>
                                ]}
                                style={{ 
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    cursor: 'pointer'
                                }}
                                onClick={() => selectPatient(patient)}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <div style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            backgroundColor: '#1890ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: 'bold'
                                        }}>
                                            {fullName.charAt(0).toUpperCase()}
                                        </div>
                                    }
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                                {fullName}
                                            </span>
                                            <Tag color={patient.status === 'active' ? 'green' : 'orange'}>
                                                {patient.status || 'unknown'}
                                            </Tag>
                                        </div>
                                    }
                                    description={
                                        <Row gutter={[16, 4]}>
                                            <Col span={12}>
                                                <Space direction="vertical" size={0}>
                                                    <span><strong>MRN:</strong> {mrn}</span>
                                                    <span><strong>Age:</strong> {age} years</span>
                                                    <span><strong>Gender:</strong> {patient.gender || 'N/A'}</span>
                                                </Space>
                                            </Col>
                                            <Col span={12}>
                                                <Space direction="vertical" size={0}>
                                                    <span><strong>Status:</strong> {patient.status || 'N/A'}</span>
                                                    <span><strong>Registration:</strong> {patient.registration_status || 'N/A'}</span>
                                                    <span><strong>Services:</strong> {patient.current_services?.join(', ') || 'None'}</span>
                                                </Space>
                                            </Col>
                                        </Row>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                />
                
                {!patientSearchLoading && patientSearchResults.length === 0 && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px', 
                        color: '#999',
                        background: '#fafafa',
                        borderRadius: '8px'
                    }}>
                        <UserOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <div style={{ fontSize: '16px', marginBottom: '8px' }}>No patients found</div>
                        <div style={{ fontSize: '14px' }}>
                            Try searching with a different term or check if patients exist in the system
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PatientDemographicsStep;